import { useEffect, useState } from 'react'
import clsx from 'clsx'
import Image from 'next/image'

/* blocks */
import Link from '@/atoms/Link/Link'
import Modal from '@/atoms/Modal/Modal'
import CarouselAlt from '../Carousel/CarouselAlt'
import BlogCard from '@/molecules/BlogCard/BlogCard'
import BlogNewsletter from '../BlogNewsletter/BlogNewsletter'
import BlogNewsletterModal from '../BlogNewsletter/BlogNewsletterModal'

/* utils */
import { getPostExcerpt } from '@/utils/functions/blog'
import useWindowSize from '@/hooks/useWindowSize'

/* assets */
import { ReactComponent as LatestBlogsIcon } from 'public/assets/icons/blog/latest-blogs.svg'

interface Props {
  featuredPost?: BlogPostList | null
  latestPosts: BlogPostList[]
  similarPosts: BlogPostList[]
}

function BlogDetailsAsideContent({
  latestPosts,
  similarPosts
}: Props): JSX.Element {
  const { isMobile } = useWindowSize()
  const [showComponent, setShowComponent] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (isMobile) {
      const timer = setTimeout(() => {
        setShowComponent(true)
        setModalOpen(true)
      }, 1 * 60 * 1000) // 1 minutes in milliseconds
      return () => clearTimeout(timer)
    } else {
      setShowComponent(true)
    }
  }, [isMobile])

  const handleCtaClick = () => {
    setModalOpen(!modalOpen)
  }

  return (
    <>
      <div className="hidden lg:block | bg-yellow-20 | rounded-md | mb-12">
        <div className="px-7 py-6">
          <h3 className="flex gap-3 | text-blue-web-60 uppercase font-bold | mb-2">
            <LatestBlogsIcon />
            Latest Blogs
          </h3>
          <hr className="border-2 border-blue-web-60 | mb-3" />
          <div>
            {latestPosts.map(post => (
              <BlogCard
                key={post.id}
                type="minimal"
                title={post.title}
                description={post.excerpt}
                categories={post.categories.nodes}
                authorName={post.author.node.name}
                date={post.date}
                slug={post.slug}
                isAside={true}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="hidden md:block mb-12">
        <BlogNewsletter
          title="Get your hands on the latest news!"
          description="Subscribe to our newsletter and stay informed about the latest trends, industry insights, exciting updates, and exclusive offers delivered directly to your inbox."
          bgColor="#FF6333"
          isAside={true}
        />
      </div>

      {showComponent ? (
        <Modal isOpen={modalOpen} toggle={handleCtaClick}>
          <div className="rounded-md overflow-y-scroll h-[80vh]">
            <BlogNewsletterModal
              title="Get your hands on the latest news!"
              bgColor="#FF6333"
            />
          </div>
        </Modal>
      ) : null}

      {/* <div className="font-body | hidden lg:block | mb-12">
        <article>
          {featuredPost ? (
            <PromotionalBanner
              date={featuredPost.date}
              image={
                featuredPost.featuredImage
                  ? {
                      url: featuredPost.featuredImage?.node.sourceUrl,
                      altText: featuredPost.featuredImage?.node.altText,
                      width:
                        featuredPost.featuredImage?.node.mediaDetails.width,
                      height:
                        featuredPost.featuredImage?.node.mediaDetails.height
                    }
                  : null
              }
              alt={featuredPost.featuredImage?.node.altText}
              slug={`/blog/${featuredPost.slug}/`}
              categories={featuredPost.categories.nodes}
              title={featuredPost.title}
              isFirstFold={true}
              isNew={true}
              bgColor="#7566FF"
              isAside={true}
              readTime={featuredPost.readingTime}
            />
          ) : null}
        </article>
      </div> */}
      <div>
        {similarPosts.length > 0 ? (
          <div className="bg-green-20 | max-w-2xl mx-auto rounded-md | p-4 md:px-7 md:py-6">
            <h3 className="font-heading | text-2xl md:text-3xl font-semibold mx-auto w-full text-center lg:text-left mb-6">
              Similar Posts
            </h3>
            <div className="hidden md:block">
              {similarPosts.map(post => (
                <BlogCard
                  key={post.id}
                  type="default"
                  date={post.date}
                  description={post.excerpt}
                  image={
                    post.featuredImage
                      ? {
                          url: post.featuredImage.node.sourceUrl,
                          altText: post.featuredImage.node.altText,
                          width: post.featuredImage.node.mediaDetails.width,
                          height: post.featuredImage.node.mediaDetails.height
                        }
                      : undefined
                  }
                  alt={post.featuredImage?.node.altText}
                  slug={`/blog/${post.slug}/`}
                  categories={post.categories.nodes}
                  title={post.title}
                  isAside={true}
                  readTime={post.readingTime}
                />
              ))}
            </div>

            <div className="md:hidden">
              <CarouselAlt
                sliderClassName="mb-10"
                autoScroll={false}
                navigationType="all"
                slides={similarPosts.map(post => {
                  const description = getPostExcerpt(post.excerpt)
                  return (
                    <Link
                      key={post.id}
                      href={`/blog/${post.slug}/`}
                      className="hover:shadow-md transition-all duration-200 ease-in"
                    >
                      <div
                        className={clsx(
                          'flex flex-col | text-black bg-white | border border-gray-30 hover:border-blue-50 transition-all duration-200 ease-in rounded-md | mx-6'
                        )}
                      >
                        {post.featuredImage ? (
                          <div>
                            <Image
                              src={post.featuredImage.node.sourceUrl}
                              alt={post.featuredImage.node.altText}
                              width={post.featuredImage.node.mediaDetails.width}
                              height={
                                post.featuredImage.node.mediaDetails.height
                              }
                              className="md:mb-3 rounded-tl-md rounded-tr-md"
                            />
                          </div>
                        ) : null}
                        <div className="font-heading | p-6">
                          {post.categories.nodes[0].name !== 'Uncategorized' ? (
                            <div className="flex items-center gap-3 | mb-4">
                              <p className="font-body text-small bg-black text-white | rounded-sm inline | px-2 py-1">
                                {post.categories.nodes[0].name}
                              </p>
                              <div
                                className={clsx(
                                  'text-caption lg:text-small font-semibold'
                                )}
                              >
                                <p>{post.readingTime} mins read</p>
                              </div>
                            </div>
                          ) : (
                            // spacer with equivalent height taken by category to keep headings at same height
                            <div className="h-5 mb-2" />
                          )}
                          <h2 className="text-base lg:text-xl font-semibold | line-clamp-2 hover:text-blue-50 | mb-1 lg:mb-2">
                            {post.title}
                          </h2>
                          {description ? (
                            <div
                              className="text-sm line-clamp-2"
                              dangerouslySetInnerHTML={{
                                __html: description
                              }}
                            />
                          ) : (
                            <div className="h-9" />
                          )}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              />
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}

export default BlogDetailsAsideContent
